import React, { createContext, useContext, useState, useEffect } from 'react'
import { setGAUserId, clearGAUserId, trackAuthEvent } from '../utils/analytics'

const AuthContext = createContext()

export function useAuth() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const [authToken, setAuthToken] = useState(localStorage.getItem('auth_token'))
  const [isDevelopmentMode, setIsDevelopmentMode] = useState(false)

  // Check authentication status on mount
  useEffect(() => {
    checkAuthentication()
  }, [])

  const checkAuthentication = async () => {
    try {
      setIsLoading(true)
      
      // Check if backend is in development mode
      const statusResponse = await fetch('/api/v1/status')
      if (statusResponse.ok) {
        const statusData = await statusResponse.json()
        const devMode = statusData?.data?.auth_system?.development_mode
        setIsDevelopmentMode(devMode)
      }
      
      const token = localStorage.getItem('auth_token')
      
      if (!token) {
        setIsAuthenticated(false)
        setUser(null)
        return
      }

      const response = await fetch('/api/v1/auth/profile', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const result = await response.json()
        // Backend returns { success: true, data: { user, profile } }
        const userData = result.success ? result.data : result
        setUser(userData)
        setIsAuthenticated(true)
        setAuthToken(token)
        
        // Set Google Analytics user ID for authenticated user
        if (userData.user?.id) {
          setGAUserId(userData.user.id.toString())
        }
      } else {
        // Token is invalid, clear it
        localStorage.removeItem('auth_token')
        setIsAuthenticated(false)
        setUser(null)
        setAuthToken(null)
        
        // Clear Google Analytics user ID
        clearGAUserId()
      }
    } catch (error) {
      console.error('Auth check failed:', error)
      setIsAuthenticated(false)
      setUser(null)
      clearGAUserId()
    } finally {
      setIsLoading(false)
    }
  }

  const login = async (email, password) => {
    try {
      // Clear any existing auth token to ensure fresh login
      localStorage.removeItem('auth_token')
      setAuthToken(null)
      
      const response = await fetch('/api/v1/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password })
      })

      const data = await response.json()

      if (response.ok) {
        // Backend returns { success: true, data: { token, user_id, email, profile } }
        const result = data.success ? data.data : data
        localStorage.setItem('auth_token', result.token)
        setAuthToken(result.token)
        setUser({ user: { id: result.user_id, email: result.email }, profile: result.profile })
        setIsAuthenticated(true)
        
        // Set Google Analytics user ID and track login event
        setGAUserId(result.user_id.toString())
        trackAuthEvent('login', result.user_id.toString())
        
        // Fetch the latest user profile to ensure we have current funds data
        await refreshUserProfile()
        
        return { success: true, user: result }
      } else {
        return { success: false, error: data.error || 'Login failed' }
      }
    } catch (error) {
      console.error('Login error:', error)
      return { success: false, error: 'Network error' }
    }
  }

  const signup = async (email, password, name = 'User') => {
    try {
      const response = await fetch('/api/v1/auth/signup-first', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email, password, name })
      })

      const data = await response.json()

      if (response.ok) {
        // Backend returns { success: true, data: { token, user_id, email, stripe_customer_id } }
        const result = data.success ? data.data : data
        localStorage.setItem('auth_token', result.token)
        setAuthToken(result.token)
        setUser({ user: { id: result.user_id, email: result.email }, stripe_customer_id: result.stripe_customer_id })
        setIsAuthenticated(true)
        
        // Set Google Analytics user ID and track signup event
        setGAUserId(result.user_id.toString())
        trackAuthEvent('signup', result.user_id.toString())
        
        // Fetch the latest user profile to ensure we have current funds data
        await refreshUserProfile()
        
        return { success: true, user: result, next_step: result.next_step }
      } else {
        return { success: false, error: data.error || 'Signup failed' }
      }
    } catch (error) {
      console.error('Signup error:', error)
      return { success: false, error: 'Network error' }
    }
  }

  const logout = () => {
    // Track logout event before clearing user data
    if (user?.user?.id) {
      trackAuthEvent('logout', user.user.id.toString())
    }
    
    localStorage.removeItem('auth_token')
    setAuthToken(null)
    setUser(null)
    setIsAuthenticated(false)
    
    // Clear Google Analytics user ID
    clearGAUserId()
  }

  const refreshUserProfile = async () => {
    // Use the token from localStorage if authToken state hasn't updated yet
    const token = authToken || localStorage.getItem('auth_token')
    if (!token) return
    
    try {
      const response = await fetch('/api/v1/auth/profile', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const result = await response.json()
        // Backend returns { success: true, data: { user, profile } }
        const userData = result.success ? result.data : result
        setUser(userData)
        
        // Ensure GA user ID is set if user data is refreshed
        if (userData.user?.id) {
          setGAUserId(userData.user.id.toString())
        }
        
        return userData
      }
    } catch (error) {
      console.error('Profile refresh failed:', error)
    }
  }

  const updateCredits = async (creditsToAdd) => {
    if (user) {
      // Update local state immediately
      const currentCredits = user.profile?.credits || 0;
      const newCredits = currentCredits + creditsToAdd;
      
      setUser({ 
        ...user, 
        profile: { 
          ...user.profile, 
          credits: newCredits 
        } 
      });
      
      // Refresh profile from server to get the latest data
      await refreshUserProfile();
    }
  }

  const updateFunds = async (fundsToAdd) => {
    if (user) {
      // Update local state immediately
      const currentFunds = user.profile?.funds || 0;
      const newFunds = currentFunds + (fundsToAdd * 100); // Convert to cents
      
      setUser({ 
        ...user, 
        profile: { 
          ...user.profile, 
          funds: newFunds 
        } 
      });
      
      // Refresh profile from server to get the latest data
      await refreshUserProfile();
    }
  }

  const handleOAuthCallback = async (session) => {
    try {
      if (!session?.access_token) {
        throw new Error('Invalid session data')
      }

      const response = await fetch('/api/v1/auth/oauth-callback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          access_token: session.access_token
        })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        const result = data.data
        localStorage.setItem('auth_token', result.token)
        setAuthToken(result.token)
        setUser({ 
          user: { id: result.user_id, email: result.email }, 
          profile: result.profile 
        })
        setIsAuthenticated(true)
        
        setGAUserId(result.user_id.toString())
        trackAuthEvent(result.is_new_user ? 'oauth_signup' : 'oauth_login', result.user_id.toString())
        
        await refreshUserProfile()
        return { success: true }
      } else {
        throw new Error(data.error || 'OAuth authentication failed')
      }
    } catch (error) {
      console.error('OAuth callback error:', error)
      return { success: false, error: error.message }
    }
  }

  const value = {
    user,
    isAuthenticated,
    isLoading,
    authToken,
    isDevelopmentMode,
    login,
    signup,
    logout,
    checkAuthentication,
    refreshUserProfile,
    updateCredits,
    updateFunds,
    handleOAuthCallback
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}